import { loadingButtonClasses } from '@mui/lab'
import type { Components, CSSObject, Theme } from '@mui/material'
import { buttonClasses, iconButtonClasses, svgIconClasses } from '@mui/material'

declare module '@mui/material/Button' {
  export interface ButtonPropsVariantOverrides {
    ghost: true
  }

  export interface ButtonPropsSizeOverrides {
    tiny: true
  }

  export interface ButtonPropsColorOverrides {
    tertiary: true
  }
}

export const MuiButton = (theme: Theme): Components['MuiButton'] => ({
  defaultProps: {
    disableElevation: true,
  },
  styleOverrides: {
    root: {
      borderRadius: +theme.shape.borderRadius * 2,
      padding: '6px 16px',//TODO
      minWidth: 'initial',
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.line.primary,
        color: theme.palette.text.disabled,
      },
      [`.${loadingButtonClasses.loadingIndicator}`]: {
        color: theme.palette.primary.main,
      },
      position: "relative",
      overflow: "hidden",
      transition: "background-color 0.3s ease",
     '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0)', 
        transition: 'background-color 0.3s ease',
      },
      '&:hover::after': {
        backgroundColor: theme.palette.background.hover
      },
      '&:focus::after': {
        backgroundColor: theme.palette.background.press
      },
    },
    sizeLarge: {
      ...(theme.typography.bodyLargeMedium__Wallfund as CSSObject),
      padding: '10px 16px',
    },
    sizeSmall: {
      ...(theme.typography.bodyLargeMedium__Wallfund as CSSObject),
      padding: '4px 16px',
      [`.${buttonClasses.startIcon}`]: {
        '&>*:nth-of-type(1)': {
          fontSize: theme.typography.pxToRem(20),
        },
      },
    },
    startIcon: {
      marginLeft: -8,
      marginRight: 4,
      '&>*:nth-of-type(1)': {
        fontSize: theme.typography.pxToRem(24),
      },
    },
    endIcon: {
      marginRight: -8,
      marginLeft: 2,
      '&>*:nth-of-type(1)': {
        fontSize: theme.typography.pxToRem(24),
      },
    },
  },
  variants: [
    {
      props: { variant: 'contained', color: 'primary' },
      style: {
        color: theme.palette.primary.lighter,
      },
    },
    {
      props: { variant: 'contained', color: 'secondary' },
      style: {
        color: theme.palette.secondary.lighter,
      },
    },
    {
      props: { variant: 'contained', color: 'error' },
      style: {
        color: theme.palette.error.lighter,
      },
    },
    {
      props: { variant: 'contained', color: 'success' },
      style: {
        color: theme.palette.success.lighter,
      },
    },
    {
      props: { variant: 'contained', color: 'warning' },
      style: {
        color: theme.palette.warning.lighter,
      },
    },
    {
      props: { variant: 'ghost' },
      style: {
        backgroundColor: theme.palette.glass?.primary.bg2,
        '&:hover': {
          backgroundColor: theme.palette.line.primary,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'primary' },
      style: {
        color: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.lighter,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'secondary' },
      style: {
        color: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: theme.palette.secondary.lighter,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'error' },
      style: {
        color: theme.palette.error.main,
        '&:hover': {
          backgroundColor: theme.palette.error.lighter,
        },
      },
    },
    {
      props: { variant: 'ghost', color: 'success' },
      style: {
        color: theme.palette.success.main,
        '&:hover': {
          backgroundColor: theme.palette.success.lighter,
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'primary' },
      style: {
        borderColor: theme.palette.primary.lighter,
      },
    },
    {
      props: { variant: 'outlined', color: 'secondary' },
      style: {
        borderColor: theme.palette.secondary.lighter,
      },
    },
    {
      props: { variant: 'outlined', color: 'error' },
      style: {
        borderColor: theme.palette.error.lighter,
      },
    },
    {
      props: { variant: 'outlined', color: 'success' },
      style: {
        borderColor: theme.palette.success.lighter,
      },
    },
    {
      props: { variant: 'outlined', color: 'warning' },
      style: {
        borderColor: theme.palette.warning.lighter,
      },
    },
    {
      props: { variant: 'text' },
      style: {
        paddingLeft: 8,
        paddingRight: 8,
        [`.${buttonClasses.startIcon}`]: {
          marginLeft: -4,
        },
        [`.${buttonClasses.endIcon}`]: {
          marginRight: -4,
        },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: {
        size: 'tiny',
      },
      style: {
        padding: theme.spacing(0.25, 0.75),
        borderRadius: theme.shape.borderRadius * 1.5,
        ...(theme.typography.caption as CSSObject),
        [`.${buttonClasses.endIcon}`]: {
          marginRight: -0.25,
          '&>*:nth-of-type(1)': {
            fontSize: theme.typography.pxToRem(20),
          },
        },
      },
    },
  ],
})

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsSizeOverrides {
    tiny: true
  }
}
export const MuiIconButton = (theme: Theme): Components['MuiIconButton'] => ({
  defaultProps: {
    TouchRippleProps: {
      center: false,
    },
  },
  styleOverrides: {
    root: {
      borderRadius: +theme.shape.borderRadius * 2,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.secondary,
      [`.${svgIconClasses.root}, svg`]: {
        fontSize: 'inherit',
      },
      [`&.${iconButtonClasses.disabled}`]: {
        backgroundColor: theme.palette.line.primary,
        color: theme.palette.text.disabled,
      },
      '&:hover': {
        backgroundColor: theme.palette.line.primary,
      },
    },
    sizeLarge: {
      fontSize: theme.typography.pxToRem(24),
    },
    sizeSmall: {
      fontSize: theme.typography.pxToRem(16),
      padding: '8px',
    },
  },
  variants: [
    {
      props: {
        size: 'tiny',
      },
      style: {
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(0.4),
      },
    },
  ],
})
